import React, { useEffect, useReducer, useRef, useState } from "react";
import {
  Avatar, Box, Button, Chip, CircularProgress, Container,
  Dialog, DialogContent, DialogTitle, Divider, Grid,
  IconButton, InputAdornment, makeStyles, Paper,
  Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Search, Close, Assignment, ChatBubbleOutline,
} from "@material-ui/icons";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  root: { flex: 1, padding: theme.spacing(2) },
  filters: {
    display: "flex", flexWrap: "wrap", gap: theme.spacing(1),
    marginBottom: theme.spacing(2), alignItems: "flex-end",
  },
  filterField: { minWidth: 160 },
  chip: { marginRight: 4 },
  row: { cursor: "pointer", "&:hover": { background: "#f5f5f5" } },
  notes: { color: "#666", fontStyle: "italic", maxWidth: 200,
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  msgBubble: {
    padding: "6px 12px", borderRadius: 12, maxWidth: "70%",
    marginBottom: 6, wordBreak: "break-word",
  },
  fromMe: { background: "#dcf8c6", alignSelf: "flex-end" },
  fromContact: { background: "#fff", alignSelf: "flex-start", border: "1px solid #eee" },
  msgRow: { display: "flex", flexDirection: "column", padding: "2px 8px" },
  msgTime: { fontSize: "0.7rem", color: "#999", textAlign: "right" },
  msgSender: { fontSize: "0.7rem", color: "#075e54", fontWeight: 600, marginBottom: 2 },
  dialogContent: {
    display: "flex", flexDirection: "column",
    maxHeight: "60vh", overflowY: "auto", background: "#e5ddd5", padding: 12,
  },
  infoGrid: { marginBottom: theme.spacing(2) },
  loadMore: { textAlign: "center", padding: theme.spacing(2) },
}));

const fmtDate = d => d ? format(parseISO(d), "dd/MM/yy HH:mm", { locale: ptBR }) : "—";

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD": return action.page === 1 ? action.data : [...state, ...action.data];
    case "RESET": return [];
    default: return state;
  }
};

export default function TicketConsultation() {
  const classes = useStyles();

  // filtros
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [searchParam, setSearchParam] = useState("");

  // resultados
  const [tickets, dispatch] = useReducer(reducer, []);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // detalhe
  const [detail, setDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const searchRef = useRef(null);

  // carregar dados para filtros
  useEffect(() => {
    api.get("/queue").then(({ data }) => setQueues(data)).catch(toastError);
    api.get("/users", { params: { pageNumber: 1 } }).then(({ data }) => setUsers(data.users || [])).catch(toastError);
    api.get("/clients").then(({ data }) => setClients(data)).catch(toastError);
  }, []);

  const fetchTickets = async (pg = 1) => {
    setLoading(true);
    try {
      const params = {
        pageNumber: pg,
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
        ...(selectedQueue && { queueId: selectedQueue.id }),
        ...(selectedUser && { userId: selectedUser.id }),
        ...(selectedClient && { clientId: selectedClient.id }),
        ...(searchParam && { searchParam }),
      };
      const { data } = await api.get("/tickets-consultation", { params });
      dispatch({ type: "LOAD", page: pg, data: data.tickets });
      setHasMore(data.hasMore);
      setCount(data.count);
      setPage(pg);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const handleSearch = () => { dispatch({ type: "RESET" }); fetchTickets(1); };
  const handleLoadMore = () => fetchTickets(page + 1);

  const openDetail = async ticket => {
    setDetail(ticket);
    setLoadingDetail(true);
    try {
      const { data } = await api.get(`/messages/${ticket.id}`);
      setMessages(data.messages || []);
    } catch (err) {
      toastError(err);
    }
    setLoadingDetail(false);
  };

  const closeDetail = () => { setDetail(null); setMessages([]); };

  return (
    <div className={classes.root}>
      <Container maxWidth="xl">
        <Typography variant="h6" gutterBottom>
          Consulta de Atendimentos
        </Typography>

        {/* Filtros */}
        <Paper style={{ padding: 16, marginBottom: 16 }}>
          <div className={classes.filters}>
            <TextField
              label="Data início" type="date" size="small" variant="outlined"
              className={classes.filterField}
              value={startDate} onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Data fim" type="date" size="small" variant="outlined"
              className={classes.filterField}
              value={endDate} onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Autocomplete
              options={queues} getOptionLabel={q => q.name}
              value={selectedQueue} onChange={(_, v) => setSelectedQueue(v)}
              style={{ minWidth: 180 }}
              renderInput={p => <TextField {...p} label="Fila" size="small" variant="outlined" />}
            />
            <Autocomplete
              options={users} getOptionLabel={u => u.name}
              value={selectedUser} onChange={(_, v) => setSelectedUser(v)}
              style={{ minWidth: 180 }}
              renderInput={p => <TextField {...p} label="Técnico" size="small" variant="outlined" />}
            />
            <Autocomplete
              options={clients}
              getOptionLabel={c => `${c.name}${c.sccmCode ? ` (${c.sccmCode})` : ""}`}
              value={selectedClient} onChange={(_, v) => setSelectedClient(v)}
              style={{ minWidth: 220 }}
              renderInput={p => <TextField {...p} label="Cliente" size="small" variant="outlined" />}
            />
            <TextField
              label="Buscar contato" size="small" variant="outlined"
              value={searchParam} onChange={e => setSearchParam(e.target.value)}
              onKeyPress={e => e.key === "Enter" && handleSearch()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleSearch}><Search /></IconButton>
                  </InputAdornment>
                )
              }}
            />
            <Button variant="contained" color="primary" onClick={handleSearch} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : "Buscar"}
            </Button>
          </div>
          {count > 0 && (
            <Typography variant="body2" color="textSecondary">
              {count} atendimento(s) encontrado(s)
            </Typography>
          )}
        </Paper>

        {/* Tabela */}
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Contato</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>Fila</TableCell>
                <TableCell>Técnico</TableCell>
                <TableCell>Aberto</TableCell>
                <TableCell>Fechado</TableCell>
                <TableCell>Observações</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map(t => (
                <TableRow key={t.id} className={classes.row} onClick={() => openDetail(t)}>
                  <TableCell>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>#{t.id}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                      <Avatar src={t.contact?.profilePicUrl} style={{ width: 28, height: 28 }} />
                      <Box>
                        <Typography variant="body2">{t.contact?.name}</Typography>
                        <Typography variant="caption" color="textSecondary">{t.contact?.number}</Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {t.contact?.client
                      ? <Chip size="small" label={t.contact.client.name} className={classes.chip} />
                      : <span style={{ color: "#bbb" }}>—</span>}
                  </TableCell>
                  <TableCell>
                    {t.queue
                      ? <Chip size="small" label={t.queue.name}
                          style={{ background: t.queue.color, color: "#fff" }} />
                      : "—"}
                  </TableCell>
                  <TableCell>{t.user?.name || "—"}</TableCell>
                  <TableCell>{fmtDate(t.createdAt)}</TableCell>
                  <TableCell>{fmtDate(t.closedAt)}</TableCell>
                  <TableCell>
                    <Tooltip title={t.notes || ""}>
                      <span className={classes.notes}>{t.notes || <span style={{ color: "#bbb" }}>—</span>}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small"><ChatBubbleOutline fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {tickets.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={9} align="center" style={{ color: "#aaa", padding: 32 }}>
                    Use os filtros acima e clique em Buscar
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {loading && (
            <div className={classes.loadMore}><CircularProgress size={24} /></div>
          )}
          {hasMore && !loading && (
            <div className={classes.loadMore}>
              <Button onClick={handleLoadMore} size="small">Carregar mais</Button>
            </div>
          )}
        </Paper>
      </Container>

      {/* Diálogo de detalhe */}
      {detail && (
        <Dialog open onClose={closeDetail} maxWidth="md" fullWidth>
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <span>Atendimento #{detail.id} — {detail.contact?.name}</span>
              <IconButton size="small" onClick={closeDetail}><Close /></IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {/* Resumo */}
            <Grid container spacing={2} className={classes.infoGrid}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">Fila</Typography>
                <Typography variant="body2">{detail.queue?.name || "—"}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">Técnico</Typography>
                <Typography variant="body2">{detail.user?.name || "—"}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">Cliente</Typography>
                <Typography variant="body2">{detail.contact?.client?.name || "—"}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="textSecondary">Fechado em</Typography>
                <Typography variant="body2">{fmtDate(detail.closedAt)}</Typography>
              </Grid>
              {detail.notes && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Observações</Typography>
                  <Paper variant="outlined" style={{ padding: "8px 12px", marginTop: 4 }}>
                    <Typography variant="body2">{detail.notes}</Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
            <Divider style={{ marginBottom: 12 }} />

            {/* Conversa */}
            <Typography variant="caption" color="textSecondary" style={{ marginBottom: 8, display: "block" }}>
              Conversa
            </Typography>
            <div className={classes.dialogContent}>
              {loadingDetail
                ? <CircularProgress size={24} style={{ margin: "auto" }} />
                : messages.map(m => (
                  <div key={m.id} className={classes.msgRow}>
                    {!m.fromMe && (
                      <Typography className={classes.msgSender}>{detail.contact?.name}</Typography>
                    )}
                    <div className={`${classes.msgBubble} ${m.fromMe ? classes.fromMe : classes.fromContact}`}
                      style={{ alignSelf: m.fromMe ? "flex-end" : "flex-start" }}>
                      {m.mediaUrl
                        ? m.mediaType === "image"
                          ? <img src={m.mediaUrl} alt="" style={{ maxWidth: 200, borderRadius: 8 }} />
                          : m.mediaType === "audio"
                          ? <audio controls src={m.mediaUrl} style={{ maxWidth: 220 }} />
                          : <Typography variant="body2">📎 {m.body}</Typography>
                        : <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>{m.body}</Typography>}
                      <Typography className={classes.msgTime}>
                        {m.createdAt ? format(parseISO(m.createdAt), "HH:mm") : ""}
                      </Typography>
                    </div>
                  </div>
                ))}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
